import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getTranslation } from '../../../utils/translations';
import advancedBillingService from '../../../services/advancedBillingService';
import { billingService } from '../../../services/billingService';

const PaymentPhasesSection = ({ language }) => {
  const t = getTranslation(language);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentPhases, setPaymentPhases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      const data = await billingService?.getAll();
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentPhases = async (invoiceId) => {
    try {
      const phases = await advancedBillingService?.getPaymentPhases(invoiceId);
      setPaymentPhases(phases);
    } catch (error) {
      console.error('Error fetching payment phases:', error);
    }
  };

  const handleInvoiceSelect = (invoice) => {
    setSelectedInvoice(invoice);
    fetchPaymentPhases(invoice?.id);
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-warning/10 text-warning',
      paid: 'bg-success/10 text-success',
      overdue: 'bg-error/10 text-error',
      cancelled: 'bg-muted text-muted-foreground'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const formatAmount = (value) => {
    return new Intl.NumberFormat(language === 'FR' ? 'fr-FR' : 'en-US', {
      style: 'currency',
      currency: 'EUR'
    })?.format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString(language === 'FR' ? 'fr-FR' : 'en-US');
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      {/* Invoices List */}
      <div className="lg:col-span-1 bg-card border border-border rounded-lg p-4">
        <h3 className="font-semibold text-foreground mb-4">
          {language === 'FR' ? 'Factures' : 'Invoices'}
        </h3>
        <div className="space-y-2 max-h-[600px] overflow-y-auto">
          {invoices?.map((invoice) => (
            <button
              key={invoice?.id}
              onClick={() => handleInvoiceSelect(invoice)}
              className={`w-full text-left p-3 rounded-lg border transition-all ${
                selectedInvoice?.id === invoice?.id
                  ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50 hover:bg-muted'
              }`}
            >
              <div className="font-medium text-foreground">{invoice?.invoiceNumber}</div>
              <div className="text-sm text-muted-foreground">{invoice?.clientName}</div>
              <div className="text-sm font-semibold text-foreground mt-1">
                {formatAmount(invoice?.totalAmount)}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Payment Phases Details */}
      <div className="lg:col-span-2">
        {selectedInvoice ? (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-2">
                {selectedInvoice?.invoiceNumber}
              </h3>
              <p className="text-muted-foreground">{selectedInvoice?.clientName}</p>
              <div className="mt-4 flex items-center gap-4">
                <div>
                  <span className="text-sm text-muted-foreground">{language === 'FR' ? 'Total' : 'Total'}:</span>
                  <span className="ml-2 text-lg font-bold text-foreground">
                    {formatAmount(selectedInvoice?.totalAmount)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">{language === 'FR' ? 'Payé' : 'Paid'}:</span>
                  <span className="ml-2 text-lg font-semibold text-success">
                    {formatAmount(selectedInvoice?.amountPaid)}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-muted-foreground">{language === 'FR' ? 'Solde' : 'Balance'}:</span>
                  <span className="ml-2 text-lg font-semibold text-warning">
                    {formatAmount(selectedInvoice?.balanceDue)}
                  </span>
                </div>
              </div>
            </div>

            {paymentPhases?.length > 0 ? (
              <div className="space-y-3">
                {paymentPhases?.map((phase) => (
                  <div
                    key={phase?.id}
                    className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-semibold">
                            {phase?.phaseNumber}
                          </span>
                          <h4 className="font-semibold text-foreground">{phase?.phaseName}</h4>
                        </div>
                        {phase?.phaseDescription && (
                          <p className="text-sm text-muted-foreground mb-3">{phase?.phaseDescription}</p>
                        )}
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <span className="text-muted-foreground">{t?.amount}:</span>
                            <span className="ml-2 font-semibold text-foreground">
                              {formatAmount(phase?.amount)}
                            </span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">{t?.dueDate}:</span>
                            <span className="ml-2 font-medium text-foreground">
                              {formatDate(phase?.dueDate)}
                            </span>
                          </div>
                          {phase?.paymentDate && (
                            <div>
                              <span className="text-muted-foreground">{t?.paymentDate}:</span>
                              <span className="ml-2 font-medium text-success">
                                {formatDate(phase?.paymentDate)}
                              </span>
                            </div>
                          )}
                          {phase?.paymentMethod && (
                            <div>
                              <span className="text-muted-foreground">{t?.paymentMethod}:</span>
                              <span className="ml-2 font-medium text-foreground">
                                {phase?.paymentMethod}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(phase?.status)}`}>
                        {t?.[phase?.status]}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-lg border border-border">
                <Icon name="Calendar" size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {language === 'FR' ? 'Aucune phase de paiement configurée' : 'No payment phases configured'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full bg-muted/30 rounded-lg border border-border">
            <div className="text-center">
              <Icon name="FileText" size={64} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {language === 'FR' ? 'Sélectionnez une facture pour voir les phases de paiement' : 'Select an invoice to view payment phases'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentPhasesSection;